import React from 'react';
import useStudentLectureStore from '../../../zustand/store/useStudentLectureStore';
import AppHeader from '../../../components/Dashboard/AppHeader';
import {Content} from 'antd/es/layout/layout';
import {
	Card,
	Col,
	Row,
	Typography,
	Button,
	Empty,
	Space,
	Tooltip,
	Tag,
	Badge,
	Spin,
} from 'antd';
import {
	FilePdfOutlined,
	CalendarOutlined,
	EyeOutlined,
	CheckCircleOutlined,
	LoadingOutlined,
	BarChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router';

const {Title, Text} = Typography;

const ViewLectures = () => {
	const {
		lectures,
		count,
		fetchQuizMcqs,
		setQuizInfo,
		mcqs,
		loading,
	} = useStudentLectureStore();
	const navigate = useNavigate();

	const handleStartQuiz = async ({
		teacherEmail,
		subjectName: subject,
		title: chapterName,
		className
	}) => {
		setQuizInfo({teacherEmail, subject, chapterName, className});
		await fetchQuizMcqs({teacherEmail, subject, chapterName, className});
		navigate('/quiz');
	};

	const handleQuizReport = (report) => {
		navigate('/view-chapter-report', {
			state: {report},
		});
	};

	return (
		<Content className="p-6 min-h-screen">
			<AppHeader title="📚 View All Lectures" />

			{loading ? (
				<div className="flex justify-center items-center h-[60vh]">
					<Spin
						size="large"
						indicator={
							<LoadingOutlined style={{fontSize: 36, color: '#22C55E'}} spin />
						}
						tip="Loading lectures..."
					/>
				</div>
			) : (
				<>
					{count > 0 && (
						<div className="mb-4">
							<Badge
								count={count}
								style={{backgroundColor: '#3B82F6'}}
								showZero
							>
								<Title level={4} className="!mb-0 ml-2 text-blue-700">
									Total Lectures
								</Title>
							</Badge>
						</div>
					)}

					{lectures?.length === 0 ? (
						<Empty description="No lectures uploaded yet." className="mt-10" />
					) : (
						<Row gutter={[24, 24]} className="mt-2">
							{lectures.map((lecture) => (
								<Col xs={24} sm={12} md={8} key={lecture._id}>
									<Card
										className="rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 bg-white/90 h-full flex flex-col"
										bodyStyle={{
											padding: 24,
											height: '100%',
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'space-between',
										}}
									>
										<div className="relative flex flex-col gap-4 flex-grow">
											<div className="absolute top-0 right-0 mt-2 mr-2 z-10">
												{lecture?.viewReport ? (
													<Tooltip title="Quiz Report">
														<Button
															type="text"
															icon={<BarChartOutlined />}
															className="text-blue-600 hover:bg-red-50 rounded-full"
															onClick={() => handleQuizReport(lecture)}
														/>
													</Tooltip>
												) : null}
											</div>

											<div className="flex items-center gap-2">
												<div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-400 to-pink-500 flex items-center justify-center text-white shadow-md">
													<FilePdfOutlined />
												</div>
												<div>
													<Title level={5} className="!mb-0 text-blue-700">
														{lecture.title}
													</Title>
													<Text type="secondary" className="text-sm">
														Subject: {lecture.subjectName}
													</Text>
												</div>
											</div>

											<Text
												type="secondary"
												className="flex items-center gap-2"
											>
												<CalendarOutlined className="text-green-500" />
												Uploaded:{' '}
												{dayjs(lecture.uploadedAt).format('MMMM D, YYYY')}
											</Text>

											<Tooltip title="Open lecture in new tab">
												<Button
													type="primary"
													icon={<EyeOutlined />}
													block
													size="large"
													className="bg-blue-600 hover:bg-blue-700 rounded-xl"
													onClick={() => window.open(lecture.fileUrl, '_blank')}
												>
													View Lecture
												</Button>
											</Tooltip>
										</div>

										<div className="mt-4">
											{lecture.canAttemptQuiz ? (
												<Tooltip title="Attempt the quiz for this lecture">
													<Button
														type="default"
														icon={<CheckCircleOutlined />}
														block
														size="large"
														className="border-green-500 text-green-600 hover:bg-green-50 rounded-xl"
														onClick={() => handleStartQuiz(lecture)}
													>
														Start Quiz
													</Button>
												</Tooltip>
											) : (
												<Tag
													color="orange"
													className="text-center w-full rounded-xl py-2"
												>
													Quiz not available
												</Tag>
											)}
										</div>
									</Card>
								</Col>
							))}
						</Row>
					)}
				</>
			)}
		</Content>
	);
};

export default ViewLectures;
